import { useCallback, useMemo } from "react";
import { FormLabel } from "../ui/form";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ALargeSmallIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { FieldRenderProps } from "@/lib/types";

export const FieldSize = ({
  allowAuto,
  units,
  field,
  title,
}: FieldRenderProps & {
  allowAuto: boolean;
  units: string[];
}) => {
  const activeUnit = useMemo(
    () => units.find(unit => field.value?.endsWith(unit)) || "px",
    [field.value, units],
  );

  const valueWithoutUnit = useMemo(() => {
    if (field.value === "auto") return "auto";
    return field.value?.replace(activeUnit, "");
  }, [field.value, activeUnit]);

  const handleChange = useCallback(
    (value: string | "auto") => {
      if (value === "auto") {
        field.onChange("auto");
        return;
      }

      const activeUnit =
        units.find(unit => field.value?.endsWith(unit)) || "px";
      field.onChange(`${value}${activeUnit}`);
    },
    [field, units],
  );

  const handleUnitChange = useCallback(
    (unit: string) => {
      const activeUnit =
        units.find(unit => field.value?.endsWith(unit)) || "px";
      field.onChange(`${field.value?.replace(activeUnit, "")}${unit}`);
    },
    [field, units],
  );

  return (
    <>
      <FormLabel>{title}</FormLabel>
      <div className="flex items-stretch">
        <Input
          value={valueWithoutUnit}
          onChange={e => handleChange(e.target.value)}
          className="rounded-r-none"
        />
        <div className="flex items-stretch">
          {allowAuto ? (
            <Button
              variant="secondary"
              size="iconSm"
              className="size-9 rounded-l-none rounded-r-none"
              onClick={() => handleChange("auto")}
              disabled={valueWithoutUnit === "auto"}
            >
              <ALargeSmallIcon />
            </Button>
          ) : null}
          <Select
            value={activeUnit}
            onValueChange={value => handleUnitChange(value)}
          >
            <SelectTrigger
              className={`rounded-l-none w-[${Math.max(...units.map(u => u.length)) + 1}ch]`}
            >
              <SelectValue placeholder="Theme" />
            </SelectTrigger>
            <SelectContent>
              {units.map(unit => (
                <SelectItem key={unit} value={unit}>
                  {unit}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
};
